import { ArrowRight, ChevronDown } from 'assets/icons';
import useHomeText from 'hooks/useHomeText';
import { forwardRef } from 'react';
import { CARD_CLASS, LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';
import TaskRow from './TaskRow';

const GROUP_LABELS = {
  late: 'tasks.groupLate',
  fixed: 'tasks.groupFixed',
  none: 'tasks.groupNone',
  done: 'tasks.groupDone',
};

/**
 * The groups of « Tâches du jour » in one bordered list, each header stuck
 * at the top while its rows scroll by: the late tasks (only
 * the `lateShown` most recent, a link leads to the others), the scheduled, the
 * unscheduled, then the done ones, folded unless `showDone`. Rows and headers
 * carry `data-flip`, for the rows to glide to their new place
 * (hooks/useFlipLayout).
 */
const TaskList = forwardRef(
  ({ groups, lateShown, showDone, motions, onToggleDone, onSeeLate, onToggle, onOpen }, ref) => {
    const { text } = useHomeText();
    return (
      <div ref={ref} className={`min-h-0 overflow-y-auto ${CARD_CLASS}`}>
        {groups.map((group) => {
          const label = `${text(GROUP_LABELS[group.id])} · ${group.rows.length}`;
          let rows = group.rows;
          if (group.id === 'late') rows = rows.slice(0, lateShown);
          if (group.id === 'done' && !showDone) rows = [];
          const hiddenLate = group.id === 'late' ? group.rows.length - rows.length : 0;
          return (
            <div key={group.id}>
              {group.id === 'done' ? (
                <button
                  type="button"
                  data-flip="g-done"
                  aria-expanded={showDone}
                  onClick={onToggleDone}
                  className={`sticky top-0 z-10 flex w-full items-center gap-1 text-left transition-colors hover:text-gray-700 ${LIST_GROUP_HEADER_CLASS}`}
                >
                  {label}
                  <span className={`flex transition-transform ${showDone ? '' : '-rotate-90'}`}>
                    <ChevronDown wh={14} color="currentColor" />
                  </span>
                </button>
              ) : (
                <div data-flip={`g-${group.id}`} className={`sticky top-0 z-10 flex items-center justify-between gap-3 ${LIST_GROUP_HEADER_CLASS}`}>
                  <p>{label}</p>
                  {hiddenLate > 0 && (
                    <button
                      type="button"
                      onClick={onSeeLate}
                      className="flex items-center gap-1 normal-case tracking-normal font-normal text-gray-400 transition-colors hover:text-gray-900"
                    >
                      {text('tasks.moreLate', { count: hiddenLate })}
                      <ArrowRight wh={14} color="currentColor" />
                    </button>
                  )}
                </div>
              )}
              {rows.map((item) => (
                <TaskRow
                  key={item.key}
                  item={item}
                  motion={motions[item.key]}
                  checkLabel={text(item.done ? 'tasks.markUndone' : 'tasks.markDone')}
                  lockedLabel={text('tasks.externalCheck')}
                  onToggle={onToggle}
                  onOpen={onOpen}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  },
);

export default TaskList;
